'use client';

import { useCallback } from 'react';
import type { KeyboardEvent } from 'react';

const RADIO_SELECTOR = '[role="radio"]';

const isDisabled = (el: HTMLElement): boolean =>
  el.getAttribute('aria-disabled') === 'true' ||
  ('disabled' in el && el.disabled === true);

/**
 * Keyboard model for a `role="radiogroup"` made of `role="radio"` descendants.
 * Attach the returned handler to the group element's `onKeyDown`.
 *
 * - Arrow keys move focus to the next/previous enabled radio (wrapping);
 *   Home / End jump to the first / last enabled radio. Moving focus does not
 *   select: selecting runs side effects (step auto-scroll, analytics), so
 *   this uses the APG "manual activation" variant.
 * - Space or Enter selects the focused radio by calling `.click()`, so it goes
 *   through the same handler a mouse click uses.
 *
 * Pair with a roving tabindex: 0 on the checked radio (or the first when none
 * is checked), -1 on the rest.
 */
export const useRadioGroupKeyboard = () =>
  useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const current = (event.target as HTMLElement).closest<HTMLElement>(
      RADIO_SELECTOR,
    );
    if (!current || !event.currentTarget.contains(current)) return;

    if (
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Enter'
    ) {
      event.preventDefault();
      if (!isDisabled(current)) current.click();
      return;
    }

    const radios = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(RADIO_SELECTOR),
    ).filter((el) => !isDisabled(el));
    if (radios.length === 0) return;

    const index = radios.indexOf(current);
    let next: HTMLElement | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = radios[(index + 1) % radios.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = radios[(index - 1 + radios.length) % radios.length];
        break;
      case 'Home':
        next = radios[0];
        break;
      case 'End':
        next = radios[radios.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    if (!next || next === current) return;
    next.focus();
  }, []);
